import React from "react";
import { NavLink } from "react-router-dom";
import classNames from "classnames";
import TableCell from "../../../components/SmartTable/TableCell";
import DeleteItemButton from "../../../components/SmartTable/TableFiltersActions/DeleteItemButton";
import { FILTER_TYPE_SELECT_MODE_1, FILTER_TYPE_SELECT_MODE_2 } from "../../../components/SmartTable/methods";
import { formatString } from "../../../utils/common";
import { openModalAction } from "../../../components/Modal/reducer";
import { EditButton } from "../../../components/EditButton";
import Tooltip from "../../../components/Tooltip";
import { PAGE_VIEW_USER } from "../../Core/routes";
import EmployeeCell from "./EmployeeCell";
import { MODAL_QUALIFICATION } from "./index";

/** SmartTable key used to identify the qualifications table in Redux and localStorage. */
export const TABLE_QUALIFICATIONS = "TABLE_QUALIFICATIONS";

/**
 * Columns rendered: Employee, Training, Expiry Date, Actions.
 * The Expiry Date cell shows visual indicators for: planned courses, qualifications
 * inherited from a previous organisational function, and disabled notifications.
 * The Actions column is only rendered when the user has the `canManage` permission.
 *
 * @param {boolean} canManage - Whether the current user may edit/delete qualifications.
 * @param {Function} trans - i18next translation function (`t`).
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {Array<object>} Array of Ant Design Table column descriptors.
 */
export const generateColumns = (canManage, trans, dispatch) => [
  {
    title: "trg.qualifications.employee",
    dataIndex: "id",
    filterType: FILTER_TYPE_SELECT_MODE_2,
    filterProps: {
      apiRoute: "users/all",
      searchColumns: ["name"],
      customParams: {
        order_by_column: ["last_name", "first_name"],
        order_by_type: ["asc", "asc"],
        field: ["user_is_in_organisation"],
        op: ["eq"],
        term: [1]
      },
      transformer: (employees) =>
        employees.map((item) => ({ ...item, text: `${item.name} (${item.userDetail.staff_number})` }))
    },
    onCell: (row) => ({ rowSpan: row?.qualification?.rowSpan || 0 }),
    render: (text, row) => (
      <TableCell minWidth={200}>
        <EmployeeCell row={row} />
      </TableCell>
    )
  },
  {
    title: "trg.qualifications.training",
    dataIndex: "trainingId",
    filterType: FILTER_TYPE_SELECT_MODE_1,
    filterNameKey: "trainingId",
    filterProps: {
      apiRoute: "trg/trainings?order_by_column",
      searchColumns: ["name", "code"],
      customParams: {
        order_by_column: ["name"],
        order_by_type: ["asc"],
        field: ["active"],
        op: ["eq"],
        term: [1]
      },
      transformer: (res = []) => res?.map((item) => ({ ...item, text: `${item.name} (${item.code})` })) || []
    },
    render: (text, row) => (
      <TableCell minWidth={200}>
        {formatString(
          row.qualification.training ? `${row.qualification.training.name} (${row.qualification.training.code})` : null,
          "-"
        )}
      </TableCell>
    )
  },
  {
    title: "trg.qualifications.expiry_date",
    width: 200,
    render: (row) => {
      const plannedText = trans("trg.reporting.planned");
      const fromPreviousFunctionText = trans("trg.qualifications.qualification_status_filter.from_previous_function");
      return (
        <TableCell minWidth={150}>
          <span
            className={classNames({
              "training-not-done-yet": row.qualification.notDoneYet,
              "date-in-past": row.qualification.date_in_past,
              "date-in-past-without-notification":
                row.qualification.date_in_past && !row.qualification.expiry_notification
            })}
          >
            {row.qualification.expiry_date}
          </span>
          {row.qualification.isPlanned && (
            <Tooltip title={plannedText}>
              <span className="planned-circle">{plannedText.split("")[0]}</span>
            </Tooltip>
          )}
          {row.qualification.from_previous_function && (
            <Tooltip title={fromPreviousFunctionText}>
              <span className="planned-circle">Q</span>
            </Tooltip>
          )}
          {!row.qualification.expiry_notification &&
            !row.qualification.notDoneYet &&
            !row.qualification.training.validity.no_expiry && (
              <Tooltip title={trans("trg.disabled_notification")}>
                <span className="ml-5">(*)</span>
              </Tooltip>
            )}
        </TableCell>
      );
    }
  },
  {
    title: trans("container.general_words.actions"),
    fixed: "right",
    width: 100,
    render: (row = {}) => (
      <div className="table-actions-column">
        {!row.qualification.notDoneYet && (
          <>
            {row.user_detail && (
              <Tooltip title={trans("trg.qualifications.view_history")}>
                <NavLink
                  to={{
                    pathname: `${PAGE_VIEW_USER}/${row.user_detail.user_id}`,
                    training: {
                      name: row.qualification.training.name,
                      id: row.qualification.training.id,
                      code: row.qualification.training.code,
                      qualificationId: row.qualification.id
                    }
                  }}
                  className="text-primary"
                >
                  <i className="ti-eye" />
                </NavLink>
              </Tooltip>
            )}
            {canManage && (
              <>
                <EditButton onClick={() => dispatch(openModalAction({ name: MODAL_QUALIFICATION, data: row }))} />
                <DeleteItemButton
                  tableName={TABLE_QUALIFICATIONS}
                  route={`trg/qualifications/${row.qualification.id}`}
                  content={
                    <div>
                      <div>{trans("container.confirm_modal.form_subtitle")}</div>
                    </div>
                  }
                />
              </>
            )}
          </>
        )}
      </div>
    )
  }
];
